import { zodResolver } from '@hookform/resolvers/zod';
import { DialogForm } from '@plato/ui-legacy/components/dialog/dialog-form';
import { Badge } from '@plato/ui-legacy/components/list-views/badge';
import { LoadingBlockFullPage } from '@plato/ui-legacy/components/loading/loading-block-full-page';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { LinkText } from '@plato/ui-legacy/components/ui/link-text';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { formatLongDate } from '@plato/ui-legacy/lib/date-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { roundAbsToHundred } from '@plato/ui-legacy/lib/round-abs-to-hundred';
import { IconButton } from '@plato/ui/candidates/icon-button';
import {
    DialogBody,
    DialogFooter,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
    DialogTitleGroup,
} from '@plato/ui/containers/dialog';
import {
    ArrowUpRight,
    Banknote,
    CalendarIcon,
    ChevronLeftIcon,
    CircleUserRound,
    PlusIcon,
} from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { CurrencyOrEmpty } from '../../../components/typography/currency-or-empty.tsx';
import { generateSearchParamsForCustomerInsightsNormalisedCategory } from '../../../lib/search-params-helpers.ts';
import type { InsInsightOutcomeDTO } from '../../../server/dto/ins-insight-outcome-dto.ts';
import { richTextSchema } from '../../../server/types/common-types.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import { api } from '../../../utils/api.ts';
import { useCustomerBasis } from '../../customer-detail/hooks/use-customer-basis.ts';
import { RichTextInput } from '../../rich-text/components/rich-text-input.tsx';
import { useShowCreateTaskDialog } from '../../tasks/hooks/use-show-create-task-dialog.ts';
import { getInsightName } from '../lib/insight-name.ts';
import { getInsightOutcomeStatusColor, useInsightOutcomeResponseLabel } from '../lib/insight-outcome.ts';
import { newCategoryInsightTypes } from '../lib/new-category-insight-types.ts';

const formSchema = z.object({
    textRich: richTextSchema.nullable(),
});

type InsightOutcomeFormSchema = z.infer<typeof formSchema>;

export type InsightOutcomeDetailsProps = {
    outcomeId: string;
    onBack?: () => void;
    onClose?: () => void;
};

export function InsightOutcomeDetails({ outcomeId, onBack, onClose }: InsightOutcomeDetailsProps) {
    const { data: outcome, isLoading } = api.platoUser.query.insightOutcomes.getInsightOutcomeById.useQuery(
        { outcomeId },
        { enabled: !!outcomeId },
    );

    if (isLoading || !outcome) {
        return <LoadingBlockFullPage className="mt-64" />;
    }

    return <InsightOutcomeDetailsContent outcome={outcome} onBack={onBack} onClose={onClose} />;
}

type ContentProps = {
    outcome: InsInsightOutcomeDTO;
    onBack?: () => void;
    onClose?: () => void;
};

function InsightOutcomeDetailsContent({ outcome, onBack, onClose }: ContentProps) {
    const { t } = useTranslation('insights');
    const locale = useResolvedLocale();
    const { toast } = useToast();
    const apiUtils = api.useUtils();

    const customerId = outcome.insight.customer_id;
    const responseLabel = useInsightOutcomeResponseLabel(outcome.response);
    const insightTypeName = getInsightName(t, outcome.insight);
    const { data: customerBasics } = useCustomerBasis({ customerId });
    const showCreateTaskDialog = useShowCreateTaskDialog();

    const handleCreateTask = (): void => {
        showCreateTaskDialog({
            description: `${insightTypeName}: ${outcome.category.name}\n`,
            links: [
                {
                    modelId: customerId,
                    modelType: SearchEntity.Customer,
                    title: customerBasics?.customerName ?? '',
                },
            ],
            source: 'sales_insight',
        });
    };

    // New-category insights get no link: the customer never bought the category, so
    // the sales report would show an empty, broken-looking view.
    const isWebCategory =
        outcome.insight.actionable_entity_type === 'WEB_CATEGORY' &&
        !newCategoryInsightTypes.includes(outcome.insight.insight_type);
    const showDetailsHref = isWebCategory
        ? `/customers/${encodeURIComponent(customerId)}/insights?${generateSearchParamsForCustomerInsightsNormalisedCategory(
              outcome.insight.actionable_entity_id,
              outcome.category.name,
          )}`
        : undefined;

    const {
        handleSubmit,
        control,
        formState: { isDirty, isValid },
    } = useForm<InsightOutcomeFormSchema>({
        defaultValues: {
            textRich: outcome.textRich ?? null,
        },
        resolver: zodResolver(formSchema),
        mode: 'onChange',
    });

    const { mutate: submitInsightOutcome, isPending: isSubmitting } =
        api.platoUser.command.insights.postInsightActions.submitInsightOutcome.useMutation({
            onSuccess: () => {
                void apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate();
                void apiUtils.platoUser.query.insightOutcomes.getInsightOutcomeById.invalidate({
                    outcomeId: outcome.id,
                });

                toast({
                    icon: 'success',
                    title: t('edit_outcome_toast__success__title'),
                    description: t('edit_outcome_toast__success__body'),
                });

                onClose?.();
            },
        });

    const handleFormSubmit = (data: InsightOutcomeFormSchema): void => {
        submitInsightOutcome({
            insightId: outcome.insight.id,
            status: outcome.status,
            response: outcome.response,
            textRich: data.textRich ?? undefined,
        });
    };

    return (
        <>
            <DialogHeader>
                {onBack && (
                    <IconButton label={t('back', { ns: 'common' })} onClick={onBack}>
                        <ChevronLeftIcon />
                    </IconButton>
                )}
                <DialogTitleGroup>
                    <DialogTitle>{insightTypeName}</DialogTitle>
                    {responseLabel && (
                        <Badge color={getInsightOutcomeStatusColor(outcome.response)}>{responseLabel}</Badge>
                    )}
                </DialogTitleGroup>
                <Button variant="subtle" onClick={handleCreateTask} icon={PlusIcon}>
                    {t('action__create_task')}
                </Button>
                <DialogIconClose label={t('form__close', { ns: 'common' })} />
            </DialogHeader>
            <DialogBody className="flex flex-col gap-container-cozy bg-neutral-50">
                <Controller
                    name="textRich"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <RichTextInput
                            trackingContext={{ source: 'sales_insight_outcome' }}
                            placeholder={t('edit_outcome_modal__placeholder')}
                            value={value ?? undefined}
                            onChange={onChange}
                        />
                    )}
                />

                <DialogForm.ItemList className="rounded-lg bg-white py-2 px-3 shadow-xs" grid>
                    <DialogForm.Item className="items-start">
                        <DialogForm.Label icon={ArrowUpRight}>
                            {t('edit_outcome_modal__related_to')}
                        </DialogForm.Label>
                        <DialogForm.Body className="flex flex-col gap-1 py-1.5 px-2">
                            <Text color="neutral-950">{outcome.category.name}</Text>
                            {showDetailsHref && (
                                <LinkText href={showDetailsHref} className="text-sm">
                                    {t('show_details', { ns: 'common' })}
                                </LinkText>
                            )}
                        </DialogForm.Body>
                    </DialogForm.Item>

                    <DialogForm.Item>
                        <DialogForm.Label icon={Banknote}>{t('edit_outcome_modal__value')}</DialogForm.Label>
                        <DialogForm.Body className="items-center px-2 [font-variant-numeric:lining-nums_tabular-nums]">
                            <Text color="neutral-950">
                                <CurrencyOrEmpty>
                                    {outcome.insight.absolute_number
                                        ? Math.abs(roundAbsToHundred(outcome.insight.absolute_number))
                                        : null}
                                </CurrencyOrEmpty>{' '}
                                {t('insight_outcome__est_value')}
                            </Text>
                        </DialogForm.Body>
                    </DialogForm.Item>

                    <DialogForm.Item>
                        <DialogForm.Label icon={CalendarIcon}>
                            {t('edit_outcome_modal__date')}
                        </DialogForm.Label>
                        <DialogForm.Body className="items-center px-2">
                            <Text color="neutral-950">{formatLongDate(outcome.createdAt, locale)}</Text>
                        </DialogForm.Body>
                    </DialogForm.Item>

                    <DialogForm.Item>
                        <DialogForm.Label icon={CircleUserRound}>
                            {t('edit_outcome_modal__resolved_by')}
                        </DialogForm.Label>
                        <DialogForm.Body className="items-center px-2">
                            <Text color="neutral-950">{outcome.createdBy.fullName}</Text>
                        </DialogForm.Body>
                    </DialogForm.Item>
                </DialogForm.ItemList>
            </DialogBody>
            <DialogFooter>
                <div>
                    <Button
                        variant="primary"
                        type="button"
                        onClick={handleSubmit(handleFormSubmit)}
                        disabled={!isDirty || !isValid || isSubmitting}
                        isLoading={isSubmitting}
                    >
                        {t('edit_outcome_modal__save')}
                    </Button>
                </div>
            </DialogFooter>
        </>
    );
}
